import { useTranslation } from 'react-i18next';
// Types
import { ToolNameValue } from 'app/store/pagesToolbar/pagesToolbar.types';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { PagesToolbarActions } from 'app/store/pagesToolbar/pagesToolbar.slice';
// Selectors
import { selectFocusModeEnabled } from 'app/store/AppUI/AppUI.selectors';
import { selectNewDesignEnabled, selectFiltersSidebarOpen } from 'app/store/pagesToolbar/pagesToolbar.selectors';
// Mui
import { Toolbar, Paper, Typography, Tooltip, Box } from '@mui/material';
// Icons
import {
  Settings as SettingsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Utils
import { usePagesToolbarContext } from './utils/usePagesToolbarContext';
// 
import SidebarToolsSettings from './SidebarToolsSettings';
import NewDesignButton from './Filters/NewDesignButton';

const FiltersSidebarContainer = () => {
  const { t } = useTranslation();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const focusModeEnabled = useAppSelector(selectFocusModeEnabled);
  const newDesignEnabled = useAppSelector(selectNewDesignEnabled);
  const filtersSidebarOpen = useAppSelector(selectFiltersSidebarOpen);

  const { tools, hiddenTools } = usePagesToolbarContext();
  const { open, toggle } = useToggle();

  const handleClick = () => {
    dispatch(PagesToolbarActions.toggleFiltersSidebarOpen());
  }

  const sidebarTools = newDesignEnabled ? tools.newDesignSidebar : {
    ...tools.bar,
    ...tools.additionalBar
  };

  const title = newDesignEnabled ? t('labels.filters') : t('labels.additionalTools');

  if ( !filtersSidebarOpen || focusModeEnabled ) return null;
  return (
    <Paper
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        width: 320,
        borderTop: 'none',
        borderBottom: 'none',
        '@media (max-width: 1439px)': {
          position: 'fixed',
          top: 0, right: 0, bottom: 0,
          mt: 32
        }
      }}
    >
      <Toolbar sx={{ gap: 4 }}>
        {!newDesignEnabled ? (
          <Tooltip title={t('labels.toolsSettings')}>
            <IconButton
              name="Open tools settings"
              onClick={toggle}
            ><SettingsIcon /></IconButton>
          </Tooltip>
        ) : null}
        <Typography sx={{ flexGrow: 1 }}>{title}</Typography>
        <Tooltip title={t('buttons.close')}>
          <IconButton
            name="Close sidebar"
            onClick={handleClick}
          ><CloseIcon /></IconButton>
        </Tooltip>
      </Toolbar>
      <Box sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        p: 4,
        overflowY: 'auto'
      }}>
        {Object.keys(sidebarTools).map((key) => {
          const Tool = sidebarTools[key as ToolNameValue];
          if ( newDesignEnabled ) return <Tool key={key} />;
          const isVisible = !hiddenTools.includes(key as ToolNameValue);
          if ( isVisible ) return null;
          return <Tool key={key} />
        })}
        {!newDesignEnabled && tools.settings ? (
          Object.keys(tools.settings).map((key) => {
            if ( !tools.settings ) return null;
            const Tool = tools.settings[key as ToolNameValue];
            return <Tool key={key} />
          })
        ) : null}
        <NewDesignButton />
      </Box>
      {open ? <SidebarToolsSettings onClose={toggle} /> : null }
    </Paper>
  )
}

export default FiltersSidebarContainer;
